import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Tag, TagType, TagPath, TagTreeNode, TagDateTrackingMetadata } from '@/types/tag';
import { toast } from '@/lib/toast';
import { isAuthRequired } from '@/lib/environment';
import type { Json } from '@/integrations/supabase/types';
import { getDefaultTagColor } from '@/lib/tagColors';

function mapRowToTag(row: any): Tag {
  const metadataColor = row?.metadata?.color;
  return {
    id: row.id,
    teamId: row.team_id,
    parentId: row.parent_id,
    tagType: row.tag_type as TagType,
    name: row.name,
    emoji: row.emoji,
    color: row.color || metadataColor,
    metadata: row.metadata,
    sortOrder: row.sort_order,
    createdAt: new Date(row.created_at),
    currentStateId: row.current_state_id,
    state: row.state ? {
      id: row.state.id,
      teamId: row.state.team_id,
      tagId: row.state.tag_id || null,
      stateGroupId: row.state.state_group_id || null,
      name: row.state.name,
      emoji: row.state.emoji,
      color: row.state.color,
      isOptimal: row.state.is_optimal,
      sortOrder: row.state.sort_order ?? null,
      createdAt: new Date(row.state.created_at),
    } : null,
    activeStates: row.activeStates || row.active_states ? (row.activeStates || row.active_states).map((as: any) => ({
      stateId: as.state_id || as.stateId,
      stateGroupId: as.state_group_id || as.stateGroupId,
      state: as.state ? {
        id: as.state.id,
        teamId: as.state.team_id || as.state.teamId,
        tagId: as.state.tag_id || as.state.tagId || null,
        stateGroupId: as.state.state_group_id || as.state.stateGroupId || null,
        name: as.state.name,
        emoji: as.state.emoji,
        color: as.state.color,
        isOptimal: as.state.is_optimal || as.state.isOptimal,
        sortOrder: as.state.sort_order ?? as.state.sortOrder ?? null,
        createdAt: new Date(as.state.created_at || as.state.createdAt),
      } : undefined
    })) : [],
  };
}

const deserializeTagFromCache = (row: any): Tag => {
  const createdAt = row.createdAt ?? row.created_at;
  return {
    id: row.id,
    teamId: row.teamId ?? row.team_id,
    parentId: row.parentId ?? row.parent_id ?? null,
    tagType: row.tagType ?? row.tag_type,
    name: row.name,
    emoji: row.emoji,
    color: row.color ?? row?.metadata?.color,
    metadata: row.metadata,
    sortOrder: row.sortOrder ?? row.sort_order ?? 0,
    createdAt: createdAt ? new Date(createdAt) : new Date(),
    currentStateId: row.currentStateId ?? row.current_state_id,
    state: row.state ? {
      ...row.state,
      createdAt: new Date(row.state.createdAt),
    } : null,
    activeStates: row.activeStates || [],
  };
};

export function useTags(teamId: string | null, tagType?: TagType) {
  const TAGS_CACHE_PREFIX = 'team-v-does-tags-cache:';
  const TAGS_DEV_CACHE_PREFIX = 'team-v-does-tags-cache-dev:';
  const TAGS_LAST_USER_ID_KEY = 'team-v-does-last-user-id';
  const getTagsCacheKey = (id: string) => `${TAGS_CACHE_PREFIX}${id}`;
  const getTagsDevCacheKey = (id: string) => `${TAGS_DEV_CACHE_PREFIX}${id}`;

  const loadTagsFromCache = (id: string): Tag[] => {
    if (isAuthRequired()) {
      try {
        const lastUserId = localStorage.getItem(TAGS_LAST_USER_ID_KEY);
        if (!lastUserId) return [];
      } catch {
        return [];
      }
    }
    try {
      const raw = localStorage.getItem(getTagsCacheKey(id));
      if (!raw && !isAuthRequired()) {
        const devRaw = localStorage.getItem(getTagsDevCacheKey(id));
        if (!devRaw) return [];
        return JSON.parse(devRaw).map(deserializeTagFromCache);
      }
      if (!raw) return [];
      return JSON.parse(raw).map(deserializeTagFromCache);
    } catch {
      return [];
    }
  };

  const saveTagsToCache = (id: string, nextTags: Tag[]) => {
    try {
      const payload = nextTags.map((t) => ({
        ...t,
        createdAt: t.createdAt.toISOString(),
        state: t.state ? {
          ...t.state,
          createdAt: t.state.createdAt.toISOString(),
        } : null,
      }));
      localStorage.setItem(getTagsCacheKey(id), JSON.stringify(payload));
      if (!isAuthRequired()) {
        localStorage.setItem(getTagsDevCacheKey(id), JSON.stringify(payload));
      }
    } catch {
      // ignore
    }
  };

  const [tags, setTags] = useState<Tag[]>(() => (teamId ? loadTagsFromCache(teamId) : []));
  const [loading, setLoading] = useState(() => (teamId ? loadTagsFromCache(teamId).length === 0 : true));

  // Fetch tags
  useEffect(() => {
    if (!teamId) {
      setTags([]);
      setLoading(false);
      return;
    }

    const fetchTags = async () => {
      let query = supabase
        .from('tags')
        .select('*, state:states!tags_current_state_id_fkey(*)')
        .eq('team_id', teamId)
        .order('sort_order', { ascending: true });

      if (tagType) {
        query = query.eq('tag_type', tagType);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching tags:', error);
        toast.error('Fout bij laden van tags', { channel: 'teams' });
      } else {
        const nextTags = (data || []).map(mapRowToTag);
        setTags(nextTags);
        saveTagsToCache(teamId, nextTags);
      }
      setLoading(false);
    };

    fetchTags();

    // Subscribe to realtime changes
    const channel = supabase
      .channel(`tags-${teamId}-${tagType || 'all'}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tags',
          filter: `team_id=eq.${teamId}`,
        },
        () => {
          fetchTags();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'states',
          filter: `team_id=eq.${teamId}`,
        },
        () => {
          fetchTags();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [teamId, tagType]);

  // Build tree structure from flat list
  const buildTree = useCallback((items: Tag[], parentId: string | null = null, depth = 0): TagTreeNode[] => {
    return items
      .filter(item => item.parentId === parentId)
      .map(item => ({
        ...item,
        depth,
        children: buildTree(items, item.id, depth + 1),
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, []);

  // Get tree for specific type or all
  const tagTree = useMemo(() => buildTree(tags), [tags, buildTree]);

  // Get children of a specific parent
  const getChildren = useCallback((parentId: string | null): Tag[] => {
    return tags
      .filter(t => t.parentId === parentId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [tags]);

  // Get path from root to a specific tag
  const getPath = useCallback((tagId: string): TagPath[] => {
    const path: TagPath[] = [];
    let current = tags.find(t => t.id === tagId);

    while (current) {
      path.unshift({
        id: current.id,
        name: current.name,
        emoji: current.emoji,
      });
      current = current.parentId ? tags.find(t => t.id === current!.parentId) : undefined;
    }

    return path;
  }, [tags]);

  // Get tag by ID
  const getById = useCallback((id: string): Tag | undefined => {
    return tags.find(t => t.id === id);
  }, [tags]);

  // Search tags by name
  const search = useCallback((query: string, type?: TagType): Tag[] => {
    const lowerQuery = query.toLowerCase();
    return tags.filter(t =>
      t.name.toLowerCase().includes(lowerQuery) &&
      (!type || t.tagType === type)
    );
  }, [tags]);

  // Get all descendants of a tag (recursive)
  const getDescendants = useCallback((tagId: string): Tag[] => {
    const descendants: Tag[] = [];
    const collectDescendants = (parentId: string) => {
      const children = tags.filter(t => t.parentId === parentId);
      for (const child of children) {
        descendants.push(child);
        collectDescendants(child.id);
      }
    };
    collectDescendants(tagId);
    return descendants;
  }, [tags]);

  // CRUD operations
  const addTag = useCallback(async (
    name: string,
    type: TagType,
    parentId: string | null = null,
    emoji?: string,
    options?: {
      color?: string;
      metadata?: Record<string, unknown>;
    }
  ): Promise<string | null> => {
    if (!teamId) return null;
    const trimmedName = name.trim();
    const resolvedColor = options?.color ?? getDefaultTagColor(type, trimmedName);

    // Optimistic update
    const tempId = crypto.randomUUID();
    const optimisticTag: Tag = {
      id: tempId,
      teamId,
      parentId,
      tagType: type,
      name: trimmedName,
      emoji,
      color: resolvedColor,
      metadata: options?.metadata || {},
      sortOrder: tags.filter(t => t.parentId === parentId && t.tagType === type).length,
      createdAt: new Date(),
      activeStates: [],
    };
    
    setTags(prev => [...prev, optimisticTag]);

    try {
      const { data: insertedData, error } = await supabase
        .from('tags')
        .insert([{
          team_id: teamId,
          parent_id: parentId,
          tag_type: type,
          name: trimmedName,
          emoji,
          color: resolvedColor,
          metadata: (options?.metadata || {}) as Json,
          sort_order: optimisticTag.sortOrder,
        }])
        .select('id')
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      const realId = insertedData?.id;
      if (realId && realId !== tempId) {
        setTags(prev => prev.map(t => t.id === tempId ? { ...t, id: realId } : t));
      }
      
      return realId || tempId;
    } catch (err) {
      console.error('Error adding tag:', err);
      // Rollback
      setTags(prev => prev.filter(t => t.id !== tempId));
      toast.error('Fout bij toevoegen van tag', { channel: 'teams' });
      return null;
    }
  }, [teamId, tags]);

  const updateTag = useCallback(async (
    id: string,
    updates: Partial<Pick<Tag, 'name' | 'emoji' | 'color' | 'metadata' | 'sortOrder' | 'parentId' | 'currentStateId'>>
  ): Promise<boolean> => {
    const dbUpdates: Record<string, unknown> = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.emoji !== undefined) dbUpdates.emoji = updates.emoji;
    if (updates.color !== undefined) dbUpdates.color = updates.color;
    if (updates.metadata !== undefined) dbUpdates.metadata = updates.metadata;
    if (updates.sortOrder !== undefined) dbUpdates.sort_order = updates.sortOrder;
    if (updates.parentId !== undefined) dbUpdates.parent_id = updates.parentId;
    if (updates.currentStateId !== undefined) dbUpdates.current_state_id = updates.currentStateId;

    const { error } = await supabase
      .from('tags')
      .update(dbUpdates)
      .eq('id', id);

    if (error) {
      console.error('Error updating tag:', error);
      toast.error('Fout bij bijwerken van tag', { channel: 'teams' });
      return false;
    }

    setTags(prev => prev.map(tag => {
      if (tag.id !== id) return tag;
      const next = { ...tag };
      if (updates.name !== undefined) next.name = updates.name;
      if (updates.emoji !== undefined) next.emoji = updates.emoji;
      if (updates.color !== undefined) next.color = updates.color;
      if (updates.metadata !== undefined) next.metadata = updates.metadata;
      if (updates.sortOrder !== undefined) next.sortOrder = updates.sortOrder;
      if (updates.parentId !== undefined) next.parentId = updates.parentId;
      if (updates.currentStateId !== undefined) next.currentStateId = updates.currentStateId;
      return next;
    }));

    return true;
  }, []);

  const deleteTag = useCallback(async (id: string): Promise<boolean> => {
    // Optimistic update: remove the tag and its descendants
    const descendants = getDescendants(id);
    const idsToRemove = [id, ...descendants.map(d => d.id)];
    const previousTags = [...tags];
    
    setTags(prev => prev.filter(t => !idsToRemove.includes(t.id)));

    const { error } = await supabase
      .from('tags')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting tag:', error);
      // Rollback
      setTags(previousTags);
      toast.error('Fout bij verwijderen van tag', { channel: 'teams' });
      return false;
    }

    toast.success('Tag verwijderd', { channel: 'teams' });
    return true;
  }, [tags, getDescendants]);

  const updateTagActiveState = useCallback(async (tagId: string, stateGroupId: string, stateId: string): Promise<boolean> => {
    const { error } = await (supabase
      .from('tag_active_states' as any)
      .upsert({ tag_id: tagId, state_group_id: stateGroupId, state_id: stateId }, { onConflict: 'tag_id, state_group_id' }) as any);

    if (error) {
      console.error('Error updating active state:', error);
      toast.error('Fout bij updaten van status', { channel: 'teams' });
      return false;
    }

    // Refresh tags locally to immediately show the changes (could be done more optimistically)
    // Wait, the realtime listener on `tag_active_states` is not there, but it is on `states` and `tags`.
    // It's better to update local state optimistically.
    setTags(prev => prev.map(tag => {
      if (tag.id !== tagId) return tag;

      let newActiveStates = [...(tag.activeStates || [])];
      const existingIndex = newActiveStates.findIndex(as => as.stateGroupId === stateGroupId);
      if (existingIndex !== -1) {
        newActiveStates[existingIndex] = { ...newActiveStates[existingIndex], stateId };
      } else {
        newActiveStates.push({ stateId, stateGroupId });
      }

      return {
        ...tag,
        activeStates: newActiveStates
      };
    }));

    return true;
  }, []);

  // Get effective date tracking settings for a tag (with inheritance from parents)
  const getEffectiveDateSettings = useCallback((tagId: string): TagDateTrackingMetadata => {
    const tag = tags.find(t => t.id === tagId);
    if (!tag) return {};

    // Get the tag's own settings
    const ownSettings = tag.metadata as TagDateTrackingMetadata || {};

    // If this tag explicitly overrides parent or has no parent, return own settings
    if (ownSettings.overrideParent || !tag.parentId) {
      return {
        trackDates: ownSettings.trackDates ?? false,
        defaultDiscardDays: ownSettings.defaultDiscardDays ?? null,
      };
    }

    // Otherwise, inherit from parent and merge
    const parentSettings = getEffectiveDateSettings(tag.parentId);

    return {
      trackDates: ownSettings.trackDates ?? parentSettings.trackDates ?? false,
      defaultDiscardDays: ownSettings.defaultDiscardDays ?? parentSettings.defaultDiscardDays ?? null,
    };
  }, [tags]);

  return {
    tags,
    loading,
    tagTree,
    getChildren,
    getPath,
    getById,
    getDescendants,
    getEffectiveDateSettings,
    search,
    addTag,
    updateTag,
    deleteTag,
    updateTagActiveState,
  };
}
